import React from "react";
import {
  Box,
  Grid,
  Typography,
  TextField,
  List,
  ListItem,
  ListItemText,
  ListItemButton,
  ClickAwayListener,
  Divider,
  Chip,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import InputAdornment from "@mui/material/InputAdornment";

const errorBorderSx = {
  "& .MuiOutlinedInput-root.Mui-error .MuiOutlinedInput-notchedOutline": {
    borderColor: "#ef4444",
    borderWidth: 2,
  },
};

export const TherapistSection = ({
  formData,
  handleInputChange,
  handleTherapistSearch,
  handleTherapistSelection,
  handleCustomTherapist,
  showTherapistSuggestions,
  setShowTherapistSuggestions,
  filteredTherapists,
  loadingTherapists,
  isSearching,
  isEditable,
  validationErrors,
  t,
}) => (
  <>
    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1.5 }}>
      <Box sx={{ width: 3, height: 22, backgroundColor: "#163666", borderRadius: 2, flexShrink: 0 }} />
      <Typography variant="h6" sx={{ fontWeight: 700, color: "#1a1a2e" }}>
        {t.physicianTherapist}
      </Typography>
    </Box>

    <Box
      sx={{
        mb: 3,
        p: 2,
        backgroundColor: "#eff6ff",
        borderLeft: "4px solid #163666",
        borderRadius: "0 8px 8px 0",
      }}
    >
      <Typography variant="body2" sx={{ color: "#374151", lineHeight: 1.7 }}>
        {t.therapistSelectionDescription}
      </Typography>
      <Typography variant="body2" sx={{ color: "#374151", lineHeight: 1.7, mt: 1.5, fontWeight: 600 }}>
        {t.therapistTestNumberNote}
      </Typography>
    </Box>

    {/* Therapist search */}
    <Box sx={{ mb: 3 }}>
      <Typography variant="body2" sx={{ mb: 1, fontWeight: 600, color: "#374151" }}>
        {t.searchTherapist}
        {loadingTherapists && (
          <Chip label={t.loading} size="small" sx={{ ml: 1, height: 18, fontSize: "0.65rem" }} />
        )}
        {isSearching && (
          <Chip label={t.searchMoreRecords} size="small" sx={{ ml: 1, height: 18, fontSize: "0.65rem" }} />
        )}
      </Typography>

      <Box sx={{ position: "relative" }}>
        <TextField
          fullWidth
          size="small"
          placeholder={
            loadingTherapists
              ? t.loadingTherapists
              : isSearching
              ? t.searchAllTherapists
              : t.searchTherapistHint
          }
          value={formData.therapistSearch || ""}
          onChange={(e) => handleTherapistSearch(e.target.value)}
          disabled={loadingTherapists || isSearching || !isEditable}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: "#9ca3af", fontSize: 18 }} />
              </InputAdornment>
            ),
          }}
          sx={{ backgroundColor: "#fff" }}
        />

        {showTherapistSuggestions && (
          <ClickAwayListener onClickAway={() => setShowTherapistSuggestions(false)}>
            <Box
              sx={{
                position: "absolute",
                top: "100%",
                left: 0,
                right: 0,
                zIndex: 1000,
                backgroundColor: "#fff",
                border: "1px solid #dfdfdf",
                borderRadius: "0 0 8px 8px",
                boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
                maxHeight: 220,
                overflow: "auto",
              }}
            >
              <List dense disablePadding>
                {loadingTherapists ? (
                  <ListItem>
                    <ListItemText
                      primary={t.loadingTherapists}
                      secondary={t.loadingTherapistsSecondary}
                    />
                  </ListItem>
                ) : filteredTherapists.length === 0 ? (
                  <ListItemButton
                    onClick={handleCustomTherapist}
                    sx={{ "&:hover": { backgroundColor: "#f8fafc" }, py: 1.5 }}
                  >
                    <ListItemText
                      primary={t.otherNotListed}
                      secondary={t.enterCustomTherapist}
                      primaryTypographyProps={{ fontWeight: 600, color: "#163666" }}
                    />
                  </ListItemButton>
                ) : (
                  <>
                    {filteredTherapists.map((th) => (
                      <ListItemButton
                        key={th.id}
                        onClick={() => handleTherapistSelection(th)}
                        sx={{ "&:hover": { backgroundColor: "#f8fafc" }, py: 1.25 }}
                      >
                        <ListItemText
                          primary={th.name}
                          secondary={`${th.practice || ""}${th.practice && th.city ? ", " : ""}${th.city || ""}`}
                          primaryTypographyProps={{ fontWeight: 500 }}
                        />
                      </ListItemButton>
                    ))}
                    <ListItemButton
                      onClick={handleCustomTherapist}
                      sx={{
                        "&:hover": { backgroundColor: "#f8fafc" },
                        borderTop: "1px solid #f0f0f0",
                        py: 1.25,
                      }}
                    >
                      <ListItemText
                        primary={t.otherNotListed}
                        secondary={t.enterCustomTherapist}
                        primaryTypographyProps={{ fontWeight: 600, color: "#163666" }}
                      />
                    </ListItemButton>
                  </>
                )}
              </List>
            </Box>
          </ClickAwayListener>
        )}
      </Box>
    </Box>

    {/* Custom therapist fields */}
    {formData.otherTherapist && (
      <Box
        sx={{
          p: 3,
          backgroundColor: "#f8fafc",
          borderRadius: 2,
          border: "1px solid #dfdfdf",
          mb: 2,
        }}
      >
        <Typography variant="body2" sx={{ mb: 1, fontWeight: 700, color: "#1a1a2e" }}>
          {t.customTherapistInformation}
        </Typography>
        <Box
          sx={{
            mb: 2.5,
            p: 1.5,
            backgroundColor: "#eff6ff",
            borderLeft: "3px solid #163666",
            borderRadius: "0 6px 6px 0",
          }}
        >
          <Typography variant="body2" sx={{ color: "#374151", fontSize: "0.8rem" }}>
            {t.customTherapistInfoDescription}
          </Typography>
        </Box>

        <Grid container spacing={2}>
          <Grid size={{ xs: 12 }}>
            <Typography variant="body2" sx={{ mb: 0.75, fontWeight: 600, color: "#374151" }}>
              {t.therapistName} <span style={{ color: "#ef4444" }}>*</span>
            </Typography>
            <TextField
              fullWidth
              size="small"
              placeholder={t.entertherapistName}
              value={formData.therapistName}
              onChange={(e) => handleInputChange("therapistName", e.target.value)}
              disabled={!isEditable}
              error={!!validationErrors.therapistName}
              sx={errorBorderSx}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <Typography variant="body2" sx={{ mb: 0.75, fontWeight: 600, color: "#374151" }}>
              {t.practiceName} <span style={{ color: "#ef4444" }}>*</span>
            </Typography>
            <TextField
              fullWidth
              size="small"
              placeholder={t.enterpracticeName}
              value={formData.practiceName}
              onChange={(e) => handleInputChange("practiceName", e.target.value)}
              disabled={!isEditable}
              error={!!validationErrors.practiceName}
              sx={errorBorderSx}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <Typography variant="body2" sx={{ mb: 0.75, fontWeight: 600, color: "#374151" }}>
              {t.city} <span style={{ color: "#ef4444" }}>*</span>
            </Typography>
            <TextField
              fullWidth
              size="small"
              placeholder={t.entercity}
              value={formData.city}
              onChange={(e) => handleInputChange("city", e.target.value)}
              disabled={!isEditable}
              error={!!validationErrors.therapistCity}
              sx={errorBorderSx}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <Typography variant="body2" sx={{ mb: 0.75, fontWeight: 600, color: "#374151" }}>
              {t.email} <span style={{ color: "#ef4444" }}>*</span>
            </Typography>
            <TextField
              fullWidth
              size="small"
              placeholder={t.enteremail}
              type="email"
              value={formData.email}
              onChange={(e) => handleInputChange("email", e.target.value)}
              disabled={!isEditable}
              error={!!validationErrors.therapistEmail}
              sx={errorBorderSx}
            />
          </Grid>
        </Grid>
      </Box>
    )}

    <Divider sx={{ mb: 4, borderColor: "#dfdfdf" }} />
  </>
);
